import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useLocation } from '@/contexts/LocationContext';
import { MapPin } from 'lucide-react';
import { isCourseFamilyAvailableIn } from '@/config/courseStateMapping';

interface WaOnlyCourseGateProps {
  children: ReactNode;
  /** Canonical URL of the protected page, used for the noindex fallback head. */
  canonicalUrl: string;
  courseName?: string;
  description?: string;
}

/**
 * Gates ASET / GATE course pages so they only render for users whose
 * selected location is Western Australia. Other states see a short
 * notice with a link back to the programmes hub. The fallback view is
 * marked noindex so non-WA URLs do not pollute search.
 */
const WaOnlyCourseGate = ({
  children,
  canonicalUrl,
  courseName = 'ASET / GATE programme',
  description = 'The ASET / GATE programme prepares students for entry into Perth Modern and the WA Gifted and Talented (GATE) selective schools. It is offered to families in Western Australia only.',
}: WaOnlyCourseGateProps) => {
  const { selectedState, setSelectedState } = useLocation();

  if (isCourseFamilyAvailableIn('gate', selectedState)) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{courseName} only available in Western Australia | Schoolola</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="noindex,follow" />
      </Helmet>
      <Header />
      <main className="container mx-auto px-4 py-20 md:py-28">
        <Card className="max-w-2xl mx-auto border-primary/30">
          <CardContent className="pt-8 text-center space-y-5">
            <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
              <MapPin className="h-6 w-6" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-primary">
              This course is only available in Western Australia
            </h1>
            <p className="text-muted-foreground">
              {description}
            </p>
            <div className="flex flex-wrap gap-3 justify-center pt-2">
              <Button onClick={() => setSelectedState('WA')}>
                I am in Western Australia
              </Button>
              <Button asChild variant="outline">
                <Link to="/programs">Browse programmes for my state</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

export default WaOnlyCourseGate;
